// Routines view (Dev Console): the outer loop and the trust ladder. Top:
// dev.autonomy — confirm / verify / autopilot — with what each rung lets
// the instance do on its own. Below: every routine (seeded + scheduled),
// its trigger, whether it is active and why, today's budget, the last cause
// it opened; a toggle per routine (follow the ladder / on / off) and a
// "run a tick now" button. Opening a cause's session goes through
// onOpenSession.
//
// Data: GET /dev/routines, PATCH /dev/routines/:id, POST /dev/routines,
// POST /dev/routines/tick; the ladder is config/app dev.autonomy via
// POST /dev/routines/autonomy (admins). shared/devOps/routines.js + autonomy.js are the shape.
import React, { useCallback, useEffect, useState } from 'react';
import { kitEnv } from '../../data/kitEnv.js';
import { getIdToken } from '../../data/authPlane';
import './GraphPanel.css';

const API = kitEnv('UPLOAD_API', process.env.REACT_APP_UPLOAD_API || '');
async function api(path, body, method) {
  const token = await getIdToken();
  const res = await fetch(`${API}${path}`, {
    method: method || (body === undefined ? 'GET' : 'POST'),
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `${path}: ${res.status}`);
  return json;
}

const ago = (ms) => { if (!ms) return 'never'; const m = Math.round((Date.now() - ms) / 60000); return m < 1 ? 'just now' : m < 60 ? `${m} min ago` : m < 60 * 48 ? `${Math.round(m / 60)} h ago` : `${Math.round(m / 1440)} d ago`; };
const LEVELS = [
  ['confirm', 'Confirm', 'Nothing ships or edits without a person: routines open plans only, reviews are optional, the Ship strip waits for a click.'],
  ['verify', 'Verify', 'Agents make changes and open PRs on their own (routines open working agents); every PR gets a review graph; shipping is still a person\'s click.'],
  ['autopilot', 'Autopilot', 'Verify + auto-ship (dev.ship.mode auto) gated on a green review. Production keeps its guards.'],
];
const TRIGGER_LABEL = {
  'selftest.fail': 'a self-test case fails', 'ship.verifyFailed': 'a verify loop gives up', 'feedback.down': 'a session is rated down',
  inbox: 'the runtime files a brain note', intake: 'the plane hands over a report', gardener: 'weekly, over merged changes', featureMap: 'a change reaches production', schedule: 'on a schedule',
};

export default function RoutinesPanel({ onOpenSession }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(null);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ title: '', brief: '', everyMinutes: 1440 });

  const load = useCallback(async () => {
    try { setData(await api('/dev/routines')); setErr(null); } catch (e) { setErr(e.message); }
  }, []);
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);

  const act = async (key, fn) => {
    setBusy(key); setErr(null);
    try { await fn(); await load(); } catch (e) { setErr(e.message); } finally { setBusy(null); }
  };
  const setLevel = (level) => act(`level:${level}`, () => api('/dev/routines/autonomy', { level }));
  const toggle = (r, enabled) => act(`toggle:${r.id}`, () => api(`/dev/routines/${encodeURIComponent(r.id)}`, { enabled }, 'PATCH'));
  const tick = () => act('tick', () => api('/dev/routines/tick', {}));
  const add = (e) => {
    e.preventDefault();
    act('add', async () => { await api('/dev/routines', form); setAdding(false); setForm({ title: '', brief: '', everyMinutes: 1440 }); });
  };

  const level = data?.autonomy?.level || 'confirm';
  const routines = data?.routines || [];
  return (
    <div className="gp-wrap" data-nova-note="The outer loop: the trust-ladder level (confirm / verify / autopilot) and the routines that open agent sessions from causes — self-test failures, verify loops that gave up, thumbs-downs, brain notes, plane reports, the weekly gardener, feature-map refresh after a production ship">
      <section className="gp-start" aria-label="Trust ladder">
        <div className="gp-start-row">
          <strong style={{ fontSize: 'var(--fs-sm)' }}>Trust ladder</strong>
          <div className="cs-seg" role="tablist" aria-label="Autonomy level">
            {LEVELS.map(([id, label, tip]) => (
              <button key={id} type="button" role="tab" aria-selected={level === id} className={`devc-filter${level === id ? ' active' : ''}`} title={tip} disabled={busy != null} data-agent-guard onClick={() => setLevel(id)}>{label}</button>
            ))}
          </div>
          <span className="gp-sub">{LEVELS.find(([id]) => id === level)?.[2]}</span>
        </div>
        {data?.autonomy && (
          <div className="gp-start-row gp-sub">
            <span>ship mode <b>{data.autonomy.shipMode}</b></span>
            <span>· auto-review <b>{data.autonomy.autoReview ? 'on' : 'off'}</b></span>
            <span>· review gates auto-ship <b>{data.autonomy.reviewRequiredForAuto ? 'yes' : 'no'}</b></span>
            <span>· routines open <b>{data.autonomy.routinesMode === 'plan' ? 'plans' : 'working agents'}</b></span>
          </div>
        )}
      </section>

      <div className="gp-detail-head">
        <h3 className="gp-title">Routines</h3>
        <div className="gp-actions">
          <button type="button" className="devc-btn" onClick={() => setAdding((v) => !v)}>{adding ? 'Cancel' : 'Add scheduled routine'}</button>
          <button type="button" className="devc-btn" disabled={busy != null} onClick={tick} title="Evaluate every active routine now" data-agent-guard>{busy === 'tick' ? 'Ticking…' : 'Run a tick now'}</button>
        </div>
      </div>
      {err && <div className="devc-err" role="alert">{err}</div>}

      {adding && (
        <form className="gp-start" onSubmit={add}>
          <input className="cs-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Title, e.g. Nightly dependency audit" required />
          <textarea className="cs-input" rows={3} value={form.brief} onChange={(e) => setForm({ ...form, brief: e.target.value })} placeholder="What the session should do each time…" required />
          <div className="gp-start-row">
            <label className="gp-sub">every
              <select className="cs-input" style={{ width: 'auto', marginLeft: 'var(--sp-2)' }} value={form.everyMinutes} onChange={(e) => setForm({ ...form, everyMinutes: Number(e.target.value) })}>
                <option value={60}>hour</option><option value={360}>6 hours</option><option value={1440}>day</option><option value={10080}>week</option>
              </select>
            </label>
            <button type="submit" className="devc-btn primary" disabled={busy != null} data-agent-guard>{busy === 'add' ? 'Adding…' : 'Add (plan mode)'}</button>
          </div>
        </form>
      )}

      <ul className="gp-nodes" aria-label="Routines">
        {routines.map((r) => {
          const follows = r.enabled == null;
          return (
            <li key={r.id} style={{ alignItems: 'flex-start', flexDirection: 'column', gap: 'var(--sp-1)', padding: 'var(--sp-2)', border: '1px solid var(--border)', borderRadius: 'var(--r-md)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)', width: '100%' }}>
                <span className={`gp-dot tone-${r.active ? 'green' : 'gray'}`} title={r.active ? 'active' : 'inactive'} />
                <span className="gp-node-label" style={{ flex: 1 }}>{r.title || r.id}</span>
                <span className="gp-sub">{r.template === 'plan' ? 'plans' : 'works'} · {r.runs?.count || 0}/{r.maxPerDay ?? '·'} today</span>
                <div className="cs-seg" role="group" aria-label={`${r.title || r.id} enabled`}>
                  {[[null, 'ladder', 'Follow the trust ladder (active at verify / autopilot)'], [true, 'on', 'Always active'], [false, 'off', 'Never']].map(([val, label, tip]) => (
                    <button key={String(val)} type="button" className={`devc-filter${(val === null ? follows : r.enabled === val) ? ' active' : ''}`} title={tip} disabled={busy != null} data-agent-guard onClick={() => toggle(r, val)}>{label}</button>
                  ))}
                </div>
              </div>
              <div className="gp-sub" style={{ width: '100%' }}>
                when {TRIGGER_LABEL[r.trigger] || r.trigger}{r.schedule?.everyMinutes ? ` (every ${r.schedule.everyMinutes} min)` : ''} · last run {ago(r.lastRunAt)}
                {r.lastCause?.key && (
                  <> · last cause <code>{r.lastCause.key}</code>{r.lastCause.sessionId && onOpenSession && <> (<button type="button" className="devc-link" onClick={() => onOpenSession(r.lastCause.sessionId)}>open session</button>)</>}</>
                )}
                {r.lastTick?.skipped?.length ? <> · skipped {r.lastTick.skipped.length} ({r.lastTick.skipped[0].why})</> : null}
                {r.lastError && <> · <span style={{ color: 'var(--danger)' }}>{r.lastError}</span></>}
              </div>
            </li>
          );
        })}
        {!routines.length && !err && <li className="gp-sub">Loading…</li>}
      </ul>
    </div>
  );
}
